import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { CheckBox } from "ui/components/CheckBox";
import { PAYMENT_OPTIONS, WARRANTY_OPTIONS } from "services/api/sales/listings/consts";
import { getListingDefaultsAPI } from "services/api/settings/listings/api";
import { initialListingDefaultsForm } from "services/api/settings/listings/consts";
import { ListingDefaultsFormValues } from "services/api/settings/listings/types";
import { ListingDefaultsDetailCardProps } from "./types";
import ListingDefaultsEditCard from "./components/ListingDefaultsEditCard/ListingDefaultsEditCard";
import ListingTemplatesRow from "./components/ListingTemplatesRow/ListingTemplatesRow";

const PRICE_INCLUDES_IMPORT_OPTIONS = [
  { value: "with", text: "Cijena uključuje uvoz" },
  { value: "without", text: "Cijena ne uključuje uvoz" },
];

// Read-only detalji renderiraju ista polja kao edit forma, ali disabled —
// isti obrazac kao DocumentDetailPage: naslov/opis grupe lijevo, polja desno,
// grupe odvojene crtama (divide-y).
const noop = () => {};

const Row: React.FC<{ title: string; description: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  description,
  action,
  children,
}) => (
  <div className="flex flex-col xl:flex-row gap-6 pt-2 pb-3 xl:gap-32">
    <div className="flex flex-col flex-1 max-w-[300px]">
      <p className="text-base font-bold text-slate-600 py-3">{title}</p>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
    <div className="flex justify-between items-center flex-1 px-3 my-3">
      <div className="flex flex-col w-[400px] gap-3">{children}</div>
      {action && <div>{action}</div>}
    </div>
  </div>
);

const ListingDefaultsDetailCard: React.FC<ListingDefaultsDetailCardProps> = () => {
  const { t } = useTranslate();
  const [defaults, setDefaults] = useState<ListingDefaultsFormValues>(initialListingDefaultsForm);
  const [editValues, setEditValues] = useState<ListingDefaultsFormValues>(initialListingDefaultsForm);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const fetchDefaults = useCallback(async () => {
    try {
      const data = await getListingDefaultsAPI();
      setDefaults(data);
    } catch {
      // greške rukuje createApiCall (snackbar)
    }
  }, []);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  const handleEditClick = () => {
    setEditValues(defaults);
    setShowEditModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    fetchDefaults();
    setShowEditModal(false);
  }, [fetchDefaults]);

  const locationDisplayText =
    defaults.locationDisplay === "exact"
      ? t("admin.settings.listingdefaults.location.exact")
      : t("admin.settings.listingdefaults.location.approximate");

  return (
    <>
      <div className="divide-y divide-slate-200">
        {/* Kontakt */}
        <Row
          title={t("admin.settings.listingdefaults.contact.title")}
          description={t("admin.settings.listingdefaults.contact.description")}
          action={
            <Button
              variant="outlined"
              children={t("admin.edit")}
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          }
        >
          {/* Podaci se ne uređuju ovdje — mijenjaju se na samom korisniku. */}
          {defaults.contacts.length === 0 ? (
            <div className="text-sm text-slate-400">
              Kontakt osoba još nije odabrana.
            </div>
          ) : (
            defaults.contacts.map((contact) => (
              <div key={contact.userId} className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
                <div className="text-md font-medium text-slate-800 truncate">{contact.fullName}</div>
                <div className="text-sm text-slate-500 truncate">
                  {[contact.mobilePhoneNumber, contact.phoneNumber, contact.email]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            ))
          )}
        </Row>

        {/* Lokacija */}
        <Row
          title={t("admin.settings.listingdefaults.location.title")}
          description={t("admin.settings.listingdefaults.location.description")}
        >
          {/* Lokacija se čita iz adrese poslovnice — ništa se ne upisuje ručno. */}
          {defaults.offices.length === 0 ? (
            <div className="text-sm text-slate-400">Poslovnica još nije odabrana.</div>
          ) : (
            defaults.offices.map((office) => (
              <div key={office.officeId} className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
                <div className="text-md font-medium text-slate-800 truncate">{office.name}</div>
                <div className="text-sm text-slate-500 truncate">{office.address || "-"}</div>
              </div>
            ))
          )}
          <div>
            <TextField
              type="text"
              name="locationDisplay"
              label={t("admin.settings.listingdefaults.location.display")}
              value={locationDisplayText}
              disabled
            />
          </div>
        </Row>

        {/* Plaćanje */}
        <Row
          title={t("admin.settings.listingdefaults.payment.title")}
          description={t("admin.settings.listingdefaults.payment.description")}
        >
         
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1 mb-4">
            {PAYMENT_OPTIONS.map((opt) => (
              <CheckBox
                key={opt.code}
                name={`payment_${opt.code}`}
                checked={!!defaults.paymentOptions[opt.code]}
                onChange={noop}
                label={opt.label}
                disabled
              />
            ))}
          </div>
           <div>
            <Dropdown
              name="priceIncludesImport"
              label={t("admin.settings.listingdefaults.payment.import")}
              value={defaults.priceIncludesImport}
              options={PRICE_INCLUDES_IMPORT_OPTIONS}
              onChange={noop}
              disabled
            />
          </div>
        </Row>

        {/* Jamstvo + video */}
        <Row
          title={t("admin.settings.listingdefaults.warranty.title")}
          description={t("admin.settings.listingdefaults.warranty.description")}
        >
          <div>
            <Dropdown
              name="warrantyCode"
              label={t("admin.settings.listingdefaults.warranty.warranty")}
              value={defaults.warrantyCode}
              options={WARRANTY_OPTIONS}
              onChange={noop}
              disabled
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            <CheckBox
              name="extendedWarranty"
              checked={defaults.extendedWarranty}
              onChange={noop}
              label={t("admin.settings.listingdefaults.warranty.extended")}
              disabled
            />
            <CheckBox
              name="g1ClubWarranty"
              checked={defaults.g1ClubWarranty}
              onChange={noop}
              label={t("admin.settings.listingdefaults.warranty.g1club")}
              disabled
            />
          </div>
        </Row>

        {/* Ostalo */}
        <Row
          title={t("admin.settings.listingdefaults.other.title")}
          description={t("admin.settings.listingdefaults.other.description")}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <CheckBox
              name="videoCallViewing"
              checked={defaults.videoCallViewing}
              onChange={noop}
              label={t("admin.settings.listingdefaults.other.videocall")}
              disabled
            />
          </div>
        </Row>

        {/* Predlošci oglasa — vlastiti CRUD, ne dio defaults forme. */}
        <ListingTemplatesRow />
      </div>

      <ListingDefaultsEditCard
        show={showEditModal}
        formValues={editValues}
        setFormValues={setEditValues}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEditModal(false)}
      />
    </>
  );
};

export default memo(ListingDefaultsDetailCard);
